import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { ConfigProvider, App as AntApp } from "antd";
import { buildConfig } from "../utils/config";
import type { ThemeMode } from "../utils/theme";

interface ThemeModeValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeValue>({ mode: "light", toggle: () => {}, setMode: () => {} });

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = "osi:theme";

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggle = useCallback(() => setMode((m) => (m === "light" ? "dark" : "light")), []);
  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ConfigProvider {...buildConfig(mode)}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeModeContext.Provider>
  );
};
